import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Calendar, Clock, DollarSign, FileText, AlertCircle, CheckCircle, XCircle,
  MessageSquare, Eye, Download, RefreshCw, Users, Coffee, CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CLASSES = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  clarification_required: 'bg-blue-100 text-blue-800 border-blue-300',
};

const TYPE_LABELS = {
  regular_leave: 'Regular Leave',
  emergency_leave: 'Emergency Leave',
  paid_leave: 'Paid Leave',
  overtime: 'Overtime',
  reimbursement: 'Reimbursement',
  permission: 'Short Leave',
  advance: 'Advance',
};

const TYPE_ICONS = {
  regular_leave: Calendar,
  emergency_leave: AlertCircle,
  paid_leave: Calendar,
  overtime: Clock,
  reimbursement: DollarSign,
  permission: Coffee,
  advance: CreditCard,
};

const TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'leave', label: 'Leave (Regular + Emergency + Paid)' },
  { value: 'regular_leave', label: 'Regular Leave' },
  { value: 'emergency_leave', label: 'Emergency Leave' },
  { value: 'paid_leave', label: 'Paid Leave' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'permission', label: 'Short Leave' },
  { value: 'advance', label: 'Advance' },
];

const formatDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(amount || 0);

const summarisePeriod = (req) => {
  if (!req) return '-';
  if (req.type?.includes('leave')) {
    return `${formatDate(req.from_date)} - ${formatDate(req.to_date)} (${req.num_days || 0} day${(req.num_days || 0) === 1 ? '' : 's'})`;
  }
  if (req.type === 'overtime') {
    return `${formatDate(req.ot_date)} ${req.from_time || ''}-${req.to_time || ''} (${req.hours || 0} hrs)`;
  }
  if (req.type === 'permission') {
    return `${formatDate(req.date)} ${req.from_time || ''}-${req.to_time || ''} (${req.hours || 0} hrs)`;
  }
  if (req.type === 'reimbursement') {
    return `${(req.items || []).length} item${(req.items || []).length === 1 ? '' : 's'}`;
  }
  if (req.type === 'advance') {
    return (req.advance_type || 'salary').toUpperCase();
  }
  return '-';
};

const summariseAmount = (req) => {
  if (!req) return '';
  if (req.type === 'reimbursement') return formatCurrency(req.total_amount);
  if (req.type === 'advance') return formatCurrency(req.amount);
  return '';
};

export default function EmployeeRequestsRegisterPage() {
  const { user, getAuthHeader } = useAuth();
  const isAdmin = ['owner', 'admin', 'hr_manager'].includes(user?.role);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [viewRequest, setViewRequest] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [acting, setActing] = useState(false);

  const fetchRequests = async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { all: true };
      if (typeFilter && typeFilter !== 'all') params.request_type = typeFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (employeeFilter && employeeFilter !== 'all') params.employee_id = employeeFilter;
      const res = await axios.get(`${API_URL}/employee-requests`, { params, headers: getAuthHeader() });
      setRequests(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load employee requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); /* eslint-disable-next-line */ }, [typeFilter, fromDate, toDate, employeeFilter]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    requests.forEach((r) => {
      if (r.employee_id && !map.has(r.employee_id)) map.set(r.employee_id, r.employee_name || r.employee_id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [requests]);

  const filtered = useMemo(() => requests.filter((r) => {
    if (activeTab === 'pending' && r.status !== 'pending' && r.status !== 'clarification_required') return false;
    if (activeTab === 'approved' && r.status !== 'approved') return false;
    if (activeTab === 'rejected' && r.status !== 'rejected') return false;
    if (activeTab === 'clarification' && r.status !== 'clarification_required') return false;
    if (search) {
      const q = search.toLowerCase();
      const hit = (r.request_number || '').toLowerCase().includes(q)
        || (r.employee_name || '').toLowerCase().includes(q)
        || (r.reason || '').toLowerCase().includes(q);
      if (!hit) return false;
    }
    return true;
  }), [requests, activeTab, search]);

  const counts = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((r) => r.status === 'pending' || r.status === 'clarification_required').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
    clarification: requests.filter((r) => r.status === 'clarification_required').length,
  }), [requests]);

  const handleApprove = async (id) => {
    setActing(true);
    try {
      await axios.patch(`${API_URL}/employee-requests/${id}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Request approved');
      await fetchRequests();
      setViewRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.id) return;
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActing(true);
    try {
      await axios.patch(`${API_URL}/employee-requests/${rejectDialog.id}/reject`,
        { rejection_reason: rejectionReason },
        { headers: getAuthHeader() });
      toast.success('Request rejected');
      setRejectDialog({ open: false, id: null });
      setRejectionReason('');
      setViewRequest(null);
      await fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  const exportCsv = () => {
    const header = ['Request #', 'Employee', 'Type', 'Period/Details', 'Amount', 'Reason', 'Status', 'Submitted', 'Approver', 'Decision Date', 'Rejection Reason'];
    const rows = filtered.map((r) => [
      r.request_number || '',
      r.employee_name || '',
      TYPE_LABELS[r.type] || r.type || '',
      summarisePeriod(r).replace(/,/g, ' '),
      summariseAmount(r),
      (r.reason || '').replace(/[\r\n]+/g, ' ').replace(/,/g, ';'),
      r.status || '',
      formatDateTime(r.created_at),
      r.approved_by_name || '',
      formatDateTime(r.approved_at),
      (r.rejection_reason || '').replace(/[\r\n]+/g, ' ').replace(/,/g, ';'),
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee-requests-register-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <Card className="max-w-xl mx-auto mt-12" data-testid="register-access-denied">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>This register is only available to owner, admin, or HR manager roles.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Current role: <span className="font-mono">{user?.role || 'unknown'}</span></p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="employee-requests-register-page">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Employee Requests Register</h1>
            <p className="text-muted-foreground text-sm">Leave applications, overtime, reimbursements, permissions and advances — all employees, all statuses.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchRequests} disabled={loading} data-testid="register-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!filtered.length} data-testid="register-export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="register-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_FILTERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Employee</label>
                <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                  <SelectTrigger data-testid="register-employee-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employeeOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">From (Submitted)</label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="register-from-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">To (Submitted)</label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="register-to-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Search</label>
                <Input placeholder="Request #, employee, reason…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="register-search" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList data-testid="register-tabs">
                <TabsTrigger value="all">All ({counts.total})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({counts.pending})</TabsTrigger>
                <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
                <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
                <TabsTrigger value="clarification">Clarification ({counts.clarification})</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading requests…</p>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground" data-testid="register-empty">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No requests match the current filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request #</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Period / Details</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Approver</TableHead>
                      <TableHead>Decision</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => {
                      const Icon = TYPE_ICONS[r.type] || FileText;
                      return (
                        <TableRow key={r.id} data-testid={`register-row-${r.request_number || r.id}`}>
                          <TableCell className="font-mono font-medium">{r.request_number || '-'}</TableCell>
                          <TableCell className="font-medium">{r.employee_name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-muted-foreground" />
                              <span>{TYPE_LABELS[r.type] || r.type || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{summarisePeriod(r)}</TableCell>
                          <TableCell className="text-right font-medium">{summariseAmount(r)}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_CLASSES[r.status] || ''}>
                              {r.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {r.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {r.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                              {r.status === 'clarification_required' && <MessageSquare className="w-3 h-3 mr-1" />}
                              {r.status === 'clarification_required' ? 'Clarification' : (r.status || '').replace(/^\w/, (c) => c.toUpperCase())}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatDateTime(r.created_at)}</TableCell>
                          <TableCell className="text-sm">{r.approved_by_name || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.approved_at ? formatDateTime(r.approved_at) : '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setViewRequest(r)} data-testid={`register-view-${r.request_number || r.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {(r.status === 'pending' || r.status === 'clarification_required') && (
                                <>
                                  <Button size="sm" variant="outline" onClick={() => handleApprove(r.id)} disabled={acting} data-testid={`register-approve-${r.request_number || r.id}`}>Approve</Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setRejectDialog({ open: true, id: r.id }); setRejectionReason(''); }} disabled={acting} data-testid={`register-reject-${r.request_number || r.id}`}>Reject</Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!viewRequest} onOpenChange={(o) => !o && setViewRequest(null)}>
          <DialogContent className="max-w-2xl" data-testid="register-view-dialog">
            <DialogHeader>
              <DialogTitle>Request {viewRequest?.request_number}</DialogTitle>
              <DialogDescription>
                {viewRequest ? `${TYPE_LABELS[viewRequest.type] || viewRequest.type} · ${viewRequest.employee_name}` : ''}
              </DialogDescription>
            </DialogHeader>
            {viewRequest && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-muted-foreground text-xs">Submitted</p>
                    <p>{formatDateTime(viewRequest.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Status</p>
                    <Badge className={STATUS_CLASSES[viewRequest.status] || ''}>{viewRequest.status}</Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-muted-foreground text-xs">Period / Details</p>
                    <p>{summarisePeriod(viewRequest)}</p>
                  </div>
                  {summariseAmount(viewRequest) && (
                    <div>
                      <p className="text-muted-foreground text-xs">Amount</p>
                      <p className="font-medium">{summariseAmount(viewRequest)}</p>
                    </div>
                  )}
                  {viewRequest.reason && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs">Reason</p>
                      <p className="whitespace-pre-wrap">{viewRequest.reason}</p>
                    </div>
                  )}
                  {viewRequest.approved_by_name && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs">{viewRequest.status === 'approved' ? 'Approved' : 'Processed'} by</p>
                      <p>{viewRequest.approved_by_name} on {formatDateTime(viewRequest.approved_at)}</p>
                    </div>
                  )}
                  {viewRequest.rejection_reason && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs">Rejection Reason</p>
                      <p className="text-red-600">{viewRequest.rejection_reason}</p>
                    </div>
                  )}
                </div>
                {(viewRequest.status === 'pending' || viewRequest.status === 'clarification_required') && (
                  <DialogFooter>
                    <Button variant="outline" className="text-red-600" onClick={() => { setRejectDialog({ open: true, id: viewRequest.id }); setRejectionReason(''); }} disabled={acting}>Reject</Button>
                    <Button onClick={() => handleApprove(viewRequest.id)} disabled={acting}>Approve</Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={rejectDialog.open} onOpenChange={(o) => { if (!o) setRejectDialog({ open: false, id: null }); }}>
          <DialogContent data-testid="register-reject-dialog">
            <DialogHeader>
              <DialogTitle>Reject Request</DialogTitle>
              <DialogDescription>Provide a reason. This will be visible to the employee.</DialogDescription>
            </DialogHeader>
            <Textarea rows={4} placeholder="Reason for rejection" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} data-testid="register-reject-reason" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })} disabled={acting}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject} disabled={acting || !rejectionReason.trim()} data-testid="register-reject-confirm">
                {acting ? 'Rejecting…' : 'Confirm Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
